import { useEffect, useRef } from 'react';
import type { Track } from 'livekit-client';

/** A remote audio track and the publication id that keeps it stable across renders. */
export interface AudioSink {
  id: string;
  track: Track;
}

/**
 * Playback for every remote audio track on the call.
 *
 * livekit-client subscribes to audio automatically but deliberately does not
 * play it: a track only becomes audible once it is attached to a media
 * element. Rendering those elements here — inside the provider, so they
 * survive the call window being collapsed, expanded or closed — is what makes
 * the call actually audible.
 *
 * The elements stay out of the layout rather than being hidden with
 * `display: none`, which some browsers treat as a reason to stop playback.
 */
export function CallAudio({ sinks }: { sinks: AudioSink[] }) {
  return (
    <div className="call-audio-sinks" aria-hidden="true">
      {sinks.map((sink) => (
        <AudioSinkElement key={sink.id} track={sink.track} />
      ))}
    </div>
  );
}

function AudioSinkElement({ track }: { track: Track }) {
  const ref = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [track]);

  // eslint-disable-next-line jsx-a11y/media-has-caption
  return <audio ref={ref} autoPlay playsInline />;
}
